import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/services'
import { queryKeys, invalidateQueries } from '@/lib/query'
import type { ComplianceTarget, Organization } from '@/types'

interface CreateOrganizationData {
  name: string
  contact_email: string
  plan_tier?: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  timezone?: string
}

interface UpdateOrganizationData {
  name?: string
  contact_email?: string
  plan_tier?: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  timezone?: string
  /**
   * Compliance target override for the org.
   * - omitted (undefined) → don't touch the stored target
   * - null               → clear the stored target
   * - object             → set / replace the stored target
   * The three-way distinction matches the backend PATCH wire format.
   */
  default_target?: ComplianceTarget | null
}

/**
 * Hook to fetch all organizations (super_admin only).
 */
export function useOrganizations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.organizations.all,
    queryFn: () => api.get<Organization[]>('/organizations'),
    enabled,
  })
}

/**
 * Hook to fetch a single organization by ID.
 */
export function useOrganization(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organizations.detail(orgId),
    queryFn: () => api.get<Organization>(`/organizations/${orgId}`),
    enabled: !!orgId,
  })
}

/**
 * Hook to create a new organization (super_admin only).
 */
export function useCreateOrganization() {
  return useMutation({
    mutationFn: (data: CreateOrganizationData) => api.post<Organization>('/organizations', data),
    onSuccess: () => {
      invalidateQueries.organizations()
    },
  })
}

/**
 * Hook to update an organization.
 */
export function useUpdateOrganization(orgId: string) {
  return useMutation({
    mutationFn: (data: UpdateOrganizationData) =>
      api.put<Organization>(`/organizations/${orgId}`, data),
    onSuccess: () => {
      invalidateQueries.organizations()
    },
  })
}

/**
 * Hook to delete an organization (super_admin only).
 */
export function useDeleteOrganization() {
  return useMutation({
    mutationFn: (orgId: string) => api.delete<void>(`/organizations/${orgId}`),
    onSuccess: () => {
      invalidateQueries.organizations()
    },
  })
}

/**
 * Response from creating a logo upload URL.
 */
export interface LogoUploadURLResponse {
  upload_url: string
  logo_key: string
  expires_at: string
}

/**
 * Create a presigned upload URL for an organization logo.
 */
export function createLogoUploadURL(orgId: string, contentType: string, contentLength: number) {
  return api.post<LogoUploadURLResponse>(`/organizations/${orgId}/logo`, {
    content_type: contentType,
    content_length: contentLength,
  })
}
